import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { adminApi, errMsg, fmt } from "@/lib/api";
import { tier, TIERS } from "@/components/game/SlideCard";

const calc = (rows, thr) => {
  const total = rows.reduce((a, r) => a + Math.max(0, Number(r.weight) || 0), 0);
  if (!total) return { win: 0, rtp: 0 };
  let win = 0, rtp = 0;
  rows.forEach((r) => {
    const p = Math.max(0, Number(r.weight) || 0) / total;
    const mn = Number(r.min), mx = Number(r.max);
    const lo = Math.max(mn, thr);
    if (lo > mx) return;
    const frac = mx === mn ? 1 : (mx - lo) / (mx - mn);
    win += p * frac;
    rtp += p * frac * ((lo + mx) / 2);
  });
  return { win: win * 100, rtp: rtp * 100 };
};

export const AdminGame = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    adminApi.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  const res = useMemo(() => (s ? calc(s.multiplier_table, Number(s.win_threshold)) : null), [s]);
  if (!s) return null;

  const setRow = (i, k, v) => setS({ ...s, multiplier_table: s.multiplier_table.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        win_threshold: Number(s.win_threshold), min_bet: Number(s.min_bet), max_bet: Number(s.max_bet),
        multiplier_table: s.multiplier_table.map((r) => ({ min: Number(r.min), max: Number(r.max), weight: Number(r.weight) })),
      };
      const { data } = await adminApi.put("/admin/settings", body);
      setS(data);
      toast.success("Oyun ayarları kaydedildi");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };
  const total = s.multiplier_table.reduce((a, r) => a + (Number(r.weight) || 0), 0);

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-5">
      <div className="x-panel p-5 space-y-5">
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="x-label">Kazanma Eşiği (×)</label>
            <input data-testid="game-threshold-input" type="number" step="0.01" className="x-input" value={s.win_threshold} onChange={(e) => setS({ ...s, win_threshold: e.target.value })} />
            <div className="text-[12px] text-[var(--x-dim)] mt-1">Bu çarpanın altı kaybeder, üstü bahis × çarpan öder.</div>
          </div>
          <div>
            <label className="x-label">Min. Bahis (₺)</label>
            <input data-testid="game-minbet-input" type="number" className="x-input" value={s.min_bet} onChange={(e) => setS({ ...s, min_bet: e.target.value })} />
          </div>
          <div>
            <label className="x-label">Maks. Bahis (₺)</label>
            <input data-testid="game-maxbet-input" type="number" className="x-input" value={s.max_bet} onChange={(e) => setS({ ...s, max_bet: e.target.value })} />
          </div>
        </div>
        <div>
          <div className="font-extrabold mb-1">Çarpan Olasılık Tablosu</div>
          <div className="text-[13px] text-[var(--x-muted)] mb-3">Her satır bir çarpan aralığı. Ağırlık ne kadar yüksekse o aralığın gelme ihtimali o kadar fazla.</div>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_1fr_1fr_70px_40px] gap-2 text-[12px] font-bold text-[var(--x-muted)] px-1">
              <span>Min ×</span><span>Maks ×</span><span>Ağırlık</span><span>Olasılık</span><span />
            </div>
            {s.multiplier_table.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_70px_40px] gap-2 items-center" data-testid={`mult-row-${i}`}>
                <input type="number" step="0.01" className="x-input" style={{ borderColor: TIERS[tier(Number(r.min))].frame }} value={r.min} onChange={(e) => setRow(i, "min", e.target.value)} />
                <input type="number" step="0.01" className="x-input" value={r.max} onChange={(e) => setRow(i, "max", e.target.value)} />
                <input type="number" step="0.1" className="x-input" value={r.weight} onChange={(e) => setRow(i, "weight", e.target.value)} />
                <span className="text-sm font-bold tabular-nums">%{fmt(total ? ((Number(r.weight) || 0) / total) * 100 : 0)}</span>
                <button onClick={() => setS({ ...s, multiplier_table: s.multiplier_table.filter((_, j) => j !== i) })} className="p-2 rounded hover:bg-white/5 text-[var(--x-red)]" data-testid={`mult-row-delete-${i}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <button data-testid="mult-row-add-btn" onClick={() => setS({ ...s, multiplier_table: [...s.multiplier_table, { min: 2, max: 3, weight: 1 }] })} className="x-btn x-btn-secondary mt-3 py-2">
            <Plus size={16} /> Satır Ekle
          </button>
        </div>
        <button data-testid="game-settings-save-btn" disabled={saving} onClick={save} className="x-btn x-btn-primary w-full">Kaydet</button>
      </div>
      <div className="x-panel p-5 h-fit space-y-4">
        <div className="font-extrabold">Tahmini Sonuçlar</div>
        <div className="bg-[var(--x-bg-deep)] rounded-lg p-4">
          <div className="text-[12px] text-[var(--x-muted)] font-semibold">Kazanma Olasılığı</div>
          <div data-testid="calc-win-chance" className="text-2xl font-black">%{fmt(res.win)}</div>
        </div>
        <div className="bg-[var(--x-bg-deep)] rounded-lg p-4">
          <div className="text-[12px] text-[var(--x-muted)] font-semibold">Oyuncuya Dönüş (RTP)</div>
          <div data-testid="calc-rtp" className="text-2xl font-black">%{fmt(res.rtp)}</div>
        </div>
        <div className="bg-[var(--x-bg-deep)] rounded-lg p-4">
          <div className="text-[12px] text-[var(--x-muted)] font-semibold">Kasa Avantajı</div>
          <div data-testid="calc-house-edge" className="text-2xl font-black" style={{ color: 100 - res.rtp >= 0 ? "#00e701" : "#ed4163" }}>%{fmt(100 - res.rtp)}</div>
        </div>
        <div className="text-[12px] text-[var(--x-dim)]">Kullanıcıya özel sıradaki çarpanları “Kullanıcılar” sekmesinden belirleyebilirsiniz; belirlenen çarpanlar bu tablodan önce gelir.</div>
      </div>
    </div>
  );
};
